import { useContext, useEffect, useMemo, useState } from "react";

import { StandardConstants } from "../../../../../../../constants";
import { BasicDetailsData, LocationData, SelectData } from "../../../../../../../models";
import { GetProjectDetailsResponse as GetPublicProjectDetailsResponse } from "../../../../../../../service/publicquery";
import { getStandardSpecificSchemaDetails } from "../../../../../../../service/query";
import { Status } from "../../../../../../../service/Shared";
import { splitCoordinates } from "../../../../../../../utils";
import { ProjectContext } from "../../../ProjectContext";
import { StandardSpecificDefinition } from "../StandardSpecificSection/types";

interface UsePreAccessOverviewTabReturnData {
  publicProjectDetails: GetPublicProjectDetailsResponse | undefined;
  basicDetailsData: BasicDetailsData;
  locationData: LocationData;
  validatorData: SelectData;
  isNoStandard: boolean;
  standardSpecificDefinition: StandardSpecificDefinition | undefined;
  images: string[] | undefined;
  videoUrl: string | undefined;
  longDescription: string | undefined;
}

export const usePreAccessOverviewTab = (): UsePreAccessOverviewTabReturnData => {
  const { publicProjectDetails } = useContext(ProjectContext);
  const [standardSpecificDefinition, setStandardSpecificDefinition] = useState<StandardSpecificDefinition>();

  const basicDetailsData = useMemo<BasicDetailsData>(
    () => ({
      displayName: publicProjectDetails?.displayName,
      name: publicProjectDetails?.name,
      registryId: publicProjectDetails?.projectReference?.externalReference,
      totalArea: publicProjectDetails?.areaNetHa?.toString(),
      validatorUuid: publicProjectDetails?.validator?.uuid,
      projectType: publicProjectDetails?.projectType.displayName,
      projectStatus: publicProjectDetails?.status,
      projectTypeUuid: publicProjectDetails?.projectType.uuid,
    }),
    [publicProjectDetails]
  );

  const locationData = useMemo<LocationData>(
    () => ({
      addressCountryCode: publicProjectDetails?.addressCountryCode,
      addressRegion: publicProjectDetails?.addressRegion,
      gridReference: publicProjectDetails?.locationGridReference,
      ...splitCoordinates(publicProjectDetails?.locationCoordinates),
      locationUuid: null,
      locationRowVersion: null,
      boundaryData: null,
    }),
    [publicProjectDetails]
  );

  // Read-only, so the only validator the details section needs to name is the project's own.
  const validatorData = useMemo<SelectData>(
    () =>
      publicProjectDetails?.validator?.uuid
        ? [{ key: publicProjectDetails.validator.uuid, value: publicProjectDetails.validator.displayName }]
        : [],
    [publicProjectDetails]
  );

  const images = useMemo(() => {
    const listingImages: string[] | undefined = publicProjectDetails?.listing?.content?.images;

    return listingImages?.flatMap(
      (imgUuid) => publicProjectDetails?.listingFiles?.find((lf) => lf.uuid === imgUuid)?.file.url ?? []
    );
  }, [publicProjectDetails]);

  useEffect(() => {
    const standardSpecificSchemaUuid = publicProjectDetails?.standardSpecificSchemaUuid;

    if (!standardSpecificSchemaUuid) {
      setStandardSpecificDefinition(undefined);
      return;
    }

    getStandardSpecificSchemaDetails({ standardSpecificSchemaUuid })
      .then((response) => {
        setStandardSpecificDefinition(
          response.status === Status.Success && response.data ? response.data.definition : undefined
        );
      })
      .catch(() => setStandardSpecificDefinition(undefined));
  }, [publicProjectDetails?.standardSpecificSchemaUuid]);

  return {
    publicProjectDetails,
    basicDetailsData,
    locationData,
    validatorData,
    isNoStandard: publicProjectDetails?.standard?.uuid === StandardConstants.NO_STANDARD_UUID,
    standardSpecificDefinition,
    images,
    videoUrl: publicProjectDetails?.listing?.content?.videoUrl,
    longDescription: publicProjectDetails?.listing?.content?.longDescription,
  };
};
